import { AuthStep } from '@/bundle/Auth/ui/AuthStep/AuthStep';
import { useFormik } from 'formik';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { Box } from '@/components/Box/Box';
import { FormikCheckbox } from '@/components/form/fields/FormikCheckbox/FormikCheckbox';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { Button } from '@/components/Button/Button';
import { CancelButton } from '@/bundle/Auth/InviteUserFlow/CompleteRegistrationPage/ui/styles';
import { useAuthNavigateToLogin } from '@/bundle/Auth/hooks/useAuthNavigateToLogin';
import { Notification } from '@/components/Notification/Notification';
import {
  ADMIN_COMPLETE_REGISTRATION_VALIDATION_SCHEMA,
  CompleteRegistrationValidationSchemaType,
} from '@/bundle/Auth/AdminOnboardingFlow/AdminCompleteRegistrationPage/const';
import { ResponseErrorType } from '@/types/sharedTypes';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { AdminCompleteRegistrationPayloadType } from '@/api/v1/users/adminCompleteRegistrationApi';

type AdminCompleteRegistrationFormType = {
  onConfirm: (payload: AdminCompleteRegistrationPayloadType) => void;
  isLoading: boolean;
  error?: ResponseErrorType;
};

export const AdminCompleteRegistrationForm = ({ onConfirm, isLoading, error }: AdminCompleteRegistrationFormType) => {
  const { authNavigateToLogin } = useAuthNavigateToLogin();

  const formik = useFormik<CompleteRegistrationValidationSchemaType>({
    initialValues: {
      mobile_phone: '',
      confirmed_phone_number: false,
      confirmed_reset_password: false,
    },
    validationSchema: ADMIN_COMPLETE_REGISTRATION_VALIDATION_SCHEMA,
    onSubmit: (values: CompleteRegistrationValidationSchemaType) => {
      onConfirm({ agree_with_condition: true, mobile_phone: values.mobile_phone });
    },
  });
  const apiError = getResponseError(error) || getResponseError(error, 'agree_with_condition');
  const isDisabledSubmit = !formik.dirty || !formik.isValid;

  return (
    <AuthStep width='508px' top={0}>
      <Typography
        textAlign='start'
        variant='headline4'
        fontWeight='semibold'
        color={COLORS.grey[900]}
        marginBottom='12px'
      >
        Enter Your Phone Number
      </Typography>
      <Typography color={COLORS.grey[950]} marginBottom='28px'>
        Mobile phone number is used for added account security and identity verification.
      </Typography>

      {apiError && (
        <Notification variant='error' marginBottom='24px'>
          {apiError}
        </Notification>
      )}
      <FormikForm value={formik}>
        <Box display='flex' flexDirection='column' rowGap='28px'>
          <Box display='flex' flexDirection='column'>
            <FormikPhoneInput
              name='mobile_phone'
              label='Mobile Phone Number*'
              placeholder='Enter Mobile Phone Number With Country Code'
              apiError={error}
            />
            <Box as='label' display='flex' alignItems='flex-start' columnGap='18px' margin='12px 0 16px'>
              <FormikCheckbox
                name='confirmed_phone_number'
                checked={formik.values.confirmed_phone_number}
                label={
                  <Typography color={COLORS.grey[950]}>
                    I confirm that I am authorized to use provided phone number and agree to receive SMS messages to
                    secure my WireVault account.
                  </Typography>
                }
              />
            </Box>
            <Box as='label' display='flex' alignItems='flex-start' columnGap='18px'>
              <FormikCheckbox
                name='confirmed_reset_password'
                checked={formik.values.confirmed_reset_password}
                label={
                  <Typography color={COLORS.grey[950]}>
                    I agree to reset and use a strong password with 2-factor authentication for the email provided with
                    WireVault.
                  </Typography>
                }
              />
            </Box>
          </Box>
          <Box display='flex' flexDirection='column' rowGap='12px'>
            <Button isLoading={isLoading} onClick={formik.handleSubmit} disabled={isDisabledSubmit}>
              Complete Registration
            </Button>
            <CancelButton onClick={authNavigateToLogin}>Cancel Registration</CancelButton>
          </Box>
        </Box>
      </FormikForm>
    </AuthStep>
  );
};
